/**
 * @file: provider.tsx
 *
 * `FloatingChatProvider` — app-wide host for the docked floating chat STATE.
 * Holds the open conversation as a subject and exposes `openChat`
 * (resolve-before-open: spinner while resolving, alert on failure — a broken
 * room never renders). Mounted app-wide so any screen's chat button can open a
 * conversation.
 *
 * It does NOT render the panel — `FloatingChatHost` does, from inside the
 * navigation container (the chat coordinator uses `useNavigation`).
 */

import { resolveConversationHandle } from '@euler/app/flows/chat/utils/resolveConversationHandle';
import { useAppLoading } from '@euler/components/loading/Provider';
import { useAlert } from '@euler/functions/ux/useAlert';
import { useServiceFactory } from '@euler/services/factory';
import { useCreation } from '@euler/utils/hooks';
import { FC, ReactNode, memo, useCallback, useMemo } from 'react';
import { BehaviorSubject } from 'rxjs';
import { conversationHandleKey } from '../conversation/handle';
import { ConversationHandleQuery } from '../conversation/types';
import { FloatingChatContext, FloatingChatSelection } from './context';

export const FloatingChatProvider: FC<{ children: ReactNode }> = memo(
  ({ children }) => {
    const active$ = useCreation(
      () => new BehaviorSubject<FloatingChatSelection | null>(null),
      [],
    );
    const { chatService } = useServiceFactory();
    const loading = useAppLoading();
    const alert = useAlert();

    const openChat = useCallback(
      (query: ConversationHandleQuery) => {
        void loading.showWithAction(
          async () => {
            const handle = await resolveConversationHandle(chatService, query);
            active$.next({ handle, key: conversationHandleKey(handle) });
          },
          {
            onError: e => {
              console.error('open floating chat failed', e);
              return alert({ title: '无法打开会话', message: '请稍后重试' });
            },
          },
        );
      },
      [loading, chatService, active$, alert],
    );

    const value = useMemo(() => ({ openChat, active$ }), [openChat, active$]);

    return (
      <FloatingChatContext.Provider value={value}>
        {children}
      </FloatingChatContext.Provider>
    );
  },
);
